import { compose } from 'redux';
import { createSelector } from 'reselect';

import type { UUID, Vehicle } from '@cobliteam/common-types';

import {
  mapWebData,
  type Recurrence,
  type UpcomingMaintenanceByVehicleId,
  type UpcomingMaintenancesByStatus,
  type WebData,
} from '@cobliteam/api-lib';

import type { MaintenanceState } from '../maintenanceStore';

const selectUpcomingMaintenanceDomain = (state: MaintenanceState) =>
  state.upcomingMaintenances;

export const selectUpcomingMaintenances = compose(
  (state) => state.upcomingMaintenancesByStatusWebData,
  selectUpcomingMaintenanceDomain
);

const selectDelayedUpcomingMaintenances = createSelector(
  selectUpcomingMaintenances,
  mapWebData((m) => m.delayed)
);

const selectWarningUpcomingMaintenances = createSelector(
  selectUpcomingMaintenances,
  mapWebData((m) => m.warning)
);

const selectOnTimeUpcomingMaintenances = createSelector(
  selectUpcomingMaintenances,
  mapWebData((m) => m.onTime)
);

export const selectWarningAndDelayedUpcomingMaintenances = createSelector(
  selectDelayedUpcomingMaintenances,
  selectWarningUpcomingMaintenances,
  mapWebData(
    (
      delayedMaintenancesByVehicle: UpcomingMaintenanceByVehicleId,
      warningMaintenancesByVehicle: UpcomingMaintenanceByVehicleId
    ) => {
      const nextMaintenances = { ...delayedMaintenancesByVehicle };

      Object.entries(warningMaintenancesByVehicle).forEach(
        ([vehicleId, warningMaintenances]) => {
          if (nextMaintenances[vehicleId]) {
            nextMaintenances[vehicleId] =
              nextMaintenances[vehicleId].concat(warningMaintenances);
          } else {
            nextMaintenances[vehicleId] = warningMaintenances.slice();
          }
        }
      );

      return nextMaintenances;
    }
  )
);

export const selectNextMaintenances =
  selectWarningAndDelayedUpcomingMaintenances;

export const selectFutureMaintenances = selectOnTimeUpcomingMaintenances;

export const selectVehiclesWithReminder = (
  vehiclesWebData: WebData<Vehicle[]>
) =>
  createSelector(
    selectUpcomingMaintenances,
    () => vehiclesWebData,
    mapWebData(({ delayed, warning, onTime }, vehicles) => {
      const vehicleIds = Object.keys({
        ...delayed,
        ...warning,
        ...onTime,
      });
      return vehicles.filter((vehicle) => vehicleIds.includes(vehicle.id));
    })
  );

export const selectVehiclesWithoutReminder = (
  vehiclesWebData: WebData<Vehicle[]>
) =>
  createSelector(
    selectUpcomingMaintenances,
    () => vehiclesWebData,
    mapWebData(({ delayed, warning, onTime }, vehicles) => {
      const vehicleIds = Object.keys({
        ...delayed,
        ...warning,
        ...onTime,
      });
      return vehicles.filter((vehicle) => !vehicleIds.includes(vehicle.id));
    })
  );

const getRecurrences = (
  vehicleId: UUID,
  upcomingMaintenances: UpcomingMaintenancesByStatus
): Recurrence[] => {
  const vehicleDelayed = upcomingMaintenances.delayed[vehicleId] || [];
  const vehicleWarning = upcomingMaintenances.warning[vehicleId] || [];
  const vehicleOnTime = upcomingMaintenances.onTime[vehicleId] || [];

  const allVehicleUpcomingMaintenances = [
    ...vehicleDelayed,
    ...vehicleWarning,
    ...vehicleOnTime,
  ];

  return allVehicleUpcomingMaintenances
    .map((uM) => ({
      recurrenceInKm: uM.revisionPeriodicityInMeters / 1000,
      recurrenceName: uM.revisionName,
    }))
    .sort((a, b) => a.recurrenceInKm - b.recurrenceInKm);
};

export const selectVehiclesRecurrences = (
  vehiclesWebData: WebData<Vehicle[]>
) =>
  createSelector(
    selectVehiclesWithReminder(vehiclesWebData),
    selectUpcomingMaintenances,
    mapWebData(
      (
        vehiclesWithReminder: Vehicle[],
        upcomingMaintenances: UpcomingMaintenancesByStatus
      ) =>
        vehiclesWithReminder.map((vehicle) => ({
          vehicle,
          recurrences: getRecurrences(vehicle.id, upcomingMaintenances),
        }))
    )
  );
